import { extractErrorCode, getUserErrorMessage } from '@/shared/utils/userErrorMessage';
import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { getToastMessageApi } from '@/shared/utils/toast';
import { refreshToken } from '@/services/auth/api';
import { isAuthError } from '@/services/auth/errorCodes';
import {
  clearTokens,
  getAccessToken,
  isRefreshTokenExpired,
  isTokenExpired,
} from '@/services/auth/tokenManager';

const loginPath = '/user/login';

const httpErrorLastToast = new Map<string, number>();
function tryToastBackendError(error: any): void {

  const data: any = error?.response?.data ?? error?.info?.data;
  if (!data && !error?.info) return;
  const status: number | undefined = error?.response?.status;

  if (status === 401) return;
  const code: string | undefined =
    data?.error?.code ?? error?.info?.errorCode;

  if (code === 'D013' || code === 'D014') return;

  const msg = getUserErrorMessage(error);
  const now = Date.now();
  const last = httpErrorLastToast.get(msg) ?? 0;
  if (now - last < 1500) return;
  httpErrorLastToast.set(msg, now);
  getToastMessageApi().error(msg);
}

const AUTH_ERROR_NAME = 'AuthExpiredError';

function makeAuthError(msg: string): Error {
  const err = new Error(msg);
  err.name = AUTH_ERROR_NAME;
  return err;
}

let isRefreshing = false;

type RefreshSubscriber = {
  resolve: (config: RequestOptions) => void;
  reject: (err: Error) => void;
  config: RequestOptions;
};
let refreshSubscribers: RefreshSubscriber[] = [];

function notifyRefreshSuccess(token: string) {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  for (const { resolve, config } of subs) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${token}`,
    };
    resolve(config);
  }
}

let authRedirectPending = false;
function notifyRefreshFailed(err: Error) {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  clearTokens();
  if (!authRedirectPending) {
    authRedirectPending = true;
    getToastMessageApi().error(getUserErrorMessage(err, '登录已过期，请重新登录'));
    history.push(loginPath);

    setTimeout(() => {
      authRedirectPending = false;
    }, 1000);
  }
  for (const { reject } of subs) {
    reject(err);
  }
}

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure {
  success: boolean;
  data: any;
  message?: string;
  error?: {
    code: string;
    details?: Record<string, any>;
  };
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

const REQUEST_TIMEOUT = 15000;

export const errorConfig: RequestConfig = {
  timeout: REQUEST_TIMEOUT,
  errorConfig: {
    errorThrower: (res) => {
      const {
        success,
        message: errorMessage,
        error,
      } = res as unknown as ResponseStructure;
      if (!success) {
        const err: any = new Error(errorMessage);
        err.name = 'BizError';
        err.info = {
          errorCode: error?.code,
          errorMessage,
          data: res,
        };
        throw err;
      }
    },

    errorHandler: (error: any, opts: any) => {
      const friendlyMessage = getUserErrorMessage(error);
      try { error.friendlyMessage = friendlyMessage; error.message = friendlyMessage; } catch {}
      if (error?.name === AUTH_ERROR_NAME) {
        if (opts?.skipErrorHandler) throw error;
        return;
      }
      if (opts?.skipErrorHandler) {
        tryToastBackendError(error);
        throw error;
      }
      const code = extractErrorCode(error);
      const status = error?.response?.status;
      if (code && isAuthError(code)) {
          clearTokens();
          getToastMessageApi().error(friendlyMessage);
          history.push(loginPath);
          return;
      }
      if (status === 401) {
        getToastMessageApi().error(friendlyMessage);
        return;
      }
      if (code === 'D013' || code === 'D014') {
        getToastMessageApi().warning(friendlyMessage);
        if (code === 'D013' && history.location.pathname.startsWith('/canvas/')) {
          setTimeout(() => history.push('/projects'), 800);
        }
        return;
      }
      getToastMessageApi().error(friendlyMessage);
    },
  },

  requestInterceptors: [
    async (config: RequestOptions) => {
      const token = getAccessToken();

      if (!token) {
        return config;
      }

      if (!isTokenExpired()) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
        return config;
      }

      if (isRefreshTokenExpired()) {
        const err = makeAuthError('登录已过期，请重新登录');
        notifyRefreshFailed(err);
        throw err;
      }

      if (isRefreshing) {
        return new Promise<RequestOptions>((resolve, reject) => {
          refreshSubscribers.push({ resolve, reject, config });
        });
      }

      isRefreshing = true;
      try {
        const result = await refreshToken();
        if (result.success && result.data) {
          const newToken = result.data.accessToken;
          notifyRefreshSuccess(newToken);
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${newToken}`,
          };
          return config;
        }
        const err = makeAuthError(getUserErrorMessage(result, '登录已过期，请重新登录'));
        notifyRefreshFailed(err);
        throw err;
      } catch (e: any) {
        if (e?.name === AUTH_ERROR_NAME) throw e;
        const err = makeAuthError('登录已过期，请重新登录');
        notifyRefreshFailed(err);
        throw err;
      } finally {
        isRefreshing = false;
      }
    },
  ],

  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as { data: ResponseStructure };

      if (data?.success === false) {
        const errorCode = data.error?.code;

        if (!errorCode || !isAuthError(errorCode)) {

        }
      }

      return response;
    },
  ],
};
